import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'
import { Box, Grid, Typography } from '@mui/material'
import { EmsBatteryMode, EmsSgReadyMode } from 'src/Libs/dynamic-power-prices/types'
import BatteryBadge from '../BatteryBadge'
import SgReadyBadge from '../SgReadyBadge'

interface ModeDetailsModalProps {
  open: boolean
  onClose?: () => void
  hasSgReadyData: boolean
}

//* Modal to show details/description about the different modes for Battery and SG Ready used in the AI 360° 2.0 feature */
const ModeDetailsModal: React.FC<ModeDetailsModalProps> = ({ open, onClose, hasSgReadyData }) => {
  const { t } = useCustomTranslation()
  const [selectedTab, setSelectedTab] = useState<'battery' | 'sgReady'>('battery')

  return (
    <Dialog title={t('ai360.details-page.mode-details-modal-title')} onClose={onClose} useActions={false} maxWidth="sm">
      {hasSgReadyData && (
        <Box sx={{ mb: 3 }} display="flex" justifyContent="center">
          <TabButtonGroupBase sx={{ maxWidth: '270px' }} gap={1}>
            <TabButton onClick={() => setSelectedTab('battery')}>{t('ai360.details-page.mode-details-modal-battery')}</TabButton>
            <TabButton sx={{ whiteSpace: 'nowrap', textWrap: 'nowrap' }} onClick={() => setSelectedTab('sgReady')}>
              {t('ai360.details-page.mode-details-modal-sg-ready')}
            </TabButton>
          </TabButtonGroupBase>
        </Box>
      )}
      <Box>
        {selectedTab === 'battery' && (
          <Grid
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: 2,
            }}
          >
            <Grid item>
              <BatteryBadge status={EmsBatteryMode.NormalOperation} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-battery-normal-operation')}</Typography>
            </Grid>
            <Grid item>
              <BatteryBadge status={EmsBatteryMode.PvChargingDelayed} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-battery-pv-charging-delayed')}</Typography>
            </Grid>
            <Grid item>
              <BatteryBadge status={EmsBatteryMode.PvChargingReduced} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-battery-pv-charging-reduced')}</Typography>
            </Grid>
            <Grid item>
              <BatteryBadge status={EmsBatteryMode.BlockDischarging} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-battery-battery-discharging-blocked')}</Typography>
            </Grid>
            <Grid item>
              <BatteryBadge status={EmsBatteryMode.GridCharge} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-battery-battery-charging-from-grid')}</Typography>
            </Grid>
          </Grid>
        )}
        {selectedTab === 'sgReady' && (
          <Grid
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: 2,
            }}
          >
            <Grid item>
              <SgReadyBadge status={EmsSgReadyMode.Normal} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-sg-ready-normal-operation')}</Typography>
            </Grid>
            <Grid item>
              <SgReadyBadge status={EmsSgReadyMode.Recommended} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-sg-ready-start-up-recommendation')}</Typography>
            </Grid>
            <Grid item>
              <SgReadyBadge status={EmsSgReadyMode.Forced} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-sg-ready-start-up-command')}</Typography>
            </Grid>
            <Grid item>
              <SgReadyBadge status={EmsSgReadyMode.Blocked} />
            </Grid>
            <Grid item>
              <Typography variant="caption">{t('ai360.details-page.mode-details-modal-sg-ready-blocked')}</Typography>
            </Grid>
          </Grid>
        )}
      </Box>
    </Dialog>
  )
}

export default ModeDetailsModal
